import { Card, CardContent } from "../ui/card";
import { Meeting } from "../../types/meetings";
import { MeetingDateTime } from "./header/MeetingDateTime";
import { MeetingStats } from "./header/MeetingStats";
import { useIsMobile } from "../../hooks/use-mobile";
import { Upload, Mic } from "lucide-react";
import { DetailTitle } from "../shared/DetailTitle";
import React from "react";

interface MeetingHeaderProps {
  meeting: Meeting;
  onSave: (meeting: Meeting) => Promise<void>;
  onDelete: () => void;
}

export function MeetingHeader({
  meeting,
  onSave,
  onDelete,
}: MeetingHeaderProps) {
  const isMobile = useIsMobile();

  const handleTitleChange = (newTitle: string) => {
    const updatedMeeting = {
      ...meeting,
      title: newTitle || "Untitled Meeting",
      updated_at: new Date().toISOString(),
    };
    onSave(updatedMeeting);
  };

  const handleDateChange = (date: string) => {
    const updatedMeeting = {
      ...meeting,
      created_at: new Date(date).toISOString(),
      updated_at: new Date().toISOString(),
    };
    onSave(updatedMeeting);
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const navigateToActionItems = () => {
    const tabsElement = document.querySelector('[role="tablist"]');
    if (tabsElement) {
      const actionItemsTab = Array.from(tabsElement.children).find((child) =>
        child.textContent?.includes("Action Items")
      ) as HTMLElement;
      if (actionItemsTab) {
        actionItemsTab.click();
      }
    }
  };

  const TypeIcon = meeting.type === 'upload' ? Upload : Mic;

  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="flex flex-col gap-4">
          <DetailTitle
            title={meeting.title || ""}
            onTitleChange={handleTitleChange}
            onDelete={onDelete}
            placeholder="Untitled Meeting"
          />

          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 text-sm text-muted-foreground">
            {!isMobile && <TypeIcon className="h-5 w-5" />}
            <MeetingDateTime
              createdAt={meeting.created_at}
              onDateChange={handleDateChange}
            />

            <MeetingStats
              duration={formatDuration(meeting.duration)}
              actionItemsCount={(meeting.action_items?.length || 0)}
              onActionItemsClick={navigateToActionItems}
              status={meeting.status}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}